/**
 * Login routing — explicit returning-user vs default-home destinations.
 *
 * Replaces the hard-coded post-login redirect (LoginPage.jsx used to send
 * every role to /graph-intelligence) and the destination-losing auth bounce
 * (App.js sent unauthenticated deep links to the landing page).
 *
 * Returning user with an intended destination: the guard preserves it as
 * /login?next=<path> and login returns them there. Otherwise: DEFAULT_HOME.
 *
 * DEFAULT_HOME is the app's existing convention (/ and /login already send
 * authenticated users to /dashboard) — an INTERIM default. The canonical
 * "Home after login" surface is an open decision in
 * docs/CANONICAL_SURFACES.md; when decided, change this one constant.
 */
import { Navigate, useLocation } from "react-router-dom";

export const DEFAULT_POST_LOGIN_HOME = "/dashboard";

// Never bounce back into auth pages (redirect loops) or the public landing.
const AUTH_PATHS = ["/login", "/signup", "/"];

/**
 * Sanitize a ?next= value into a safe same-origin destination.
 * Anything that is not a plain in-app path ("/x…") — protocol-relative
 * ("//evil"), absolute URLs, auth pages — resolves to DEFAULT_HOME.
 * Open-redirect prevention is the point; never trust the parameter.
 */
export function resolvePostLoginDestination(next) {
  if (!next || typeof next !== "string") return DEFAULT_POST_LOGIN_HOME;
  if (!next.startsWith("/") || next.startsWith("//") || next.includes("\\")) {
    return DEFAULT_POST_LOGIN_HOME;
  }
  const pathOnly = next.split("?")[0];
  if (AUTH_PATHS.includes(pathOnly)) return DEFAULT_POST_LOGIN_HOME;
  return next;
}

/** Build the /login URL that preserves an intended destination. */
export function loginUrlFor(pathname, search = "") {
  const dest = `${pathname}${search}`;
  if (!dest || dest === "/" || dest.startsWith("/login")) return "/login";
  return `/login?next=${encodeURIComponent(dest)}`;
}

/**
 * Auth-guard bounce: unauthenticated users go to /login carrying the
 * intended destination, instead of being dropped on the landing page.
 */
export function RedirectToLogin() {
  const location = useLocation();
  return <Navigate to={loginUrlFor(location.pathname, location.search)} replace />;
}
